<template>
	<div class="InlineEditField" :class="{ error, editing: isEditing }">
		<div class="inline-edit-container">
			<div class="field-input">
				<div v-if="fieldType === 'Number'" class="number-input-wrapper">
					<input
						type="number"
						class="input"
						v-model="editValue"
						:disabled="!isEditing || disabled"
						@focus="onFocus"
						@blur="onBlur"
						@keyup.enter="saveChanges"
						@keyup.escape="cancelEdit"
						:min="min"
						:max="max"
						:data-cy="dataCy"
						:placeholder="placeholder"
					/>
					<span class="unit" v-if="effectiveUnit">{{ effectiveUnit }}</span>
				</div>

				<input
					v-else
					type="text"
					class="input"
					v-model="editValue"
					:disabled="!isEditing || disabled"
					@focus="onFocus"
					@blur="onBlur"
					@keyup.enter="saveChanges"
					@keyup.escape="cancelEdit"
					:data-cy="dataCy"
					:placeholder="placeholder"
				/>
			</div>

			<div class="field-action">
				<v-btn
					v-if="!isEditing"
					variant="text"
					size="default"
					color="primary"
					@click="openEditDialog"
					:disabled="disabled"
					class="edit-btn"
				>
					{{ editButtonText || $t('text.edit') }}
				</v-btn>

				<v-btn
					v-else
					color="primary"
					size="default"
					variant="text"
					@click="saveChanges"
					class="save-btn"
				>
					{{ saveButtonText || $t('text.saveChanges') }}
				</v-btn>
			</div>
		</div>

		<Dialog
			ref="editDialog"
			:confirmLabel="confirmLabel || $t('text.edit')"
			:cancelLabel="cancelLabel || $t('text.cancel')"
			:confirm-handler="onEditConfirmed"
			:showClose="true"
			:title="effectiveDialogTitle"
			height="350px"
			width="550px"
		>
			<template #content>
				<v-row align="center" justify="center">
					<v-col style="text-align: center;">
						<img class="warningDialogImage" src="@/assets/icons/icon-warning.svg" />
					</v-col>
					<v-col cols="8" class="pr-5">
						<h2>{{ effectiveDialogTitle }}</h2>
						<p>{{ effectiveDialogText }}</p>
					</v-col>
				</v-row>
			</template>
		</Dialog>
	</div>
</template>

<script>
import { field } from './FieldMixin.js'
import Dialog from '../common/Dialog.vue'

export default {
	name: 'InlineEditField',
	mixins: [field],
	components: { Dialog },
	props: {
		modelValue: [String, Number],
		dataCy: String,
		disabled: Boolean,
		fieldType: {
			type: String,
			default: 'Number',
			validator: value => ['Number', 'Text', 'Symbol'].includes(value)
		},
		unit: String,
		placeholder: String,
		dialogTitle: String,
		dialogText: String,
		confirmLabel: String,
		cancelLabel: String,
		editButtonText: String,
		saveButtonText: String,
		showDialog: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			model: null,
			editValue: null,
			isEditing: false,
			originalValue: null
		}
	},
	computed: {
		effectiveUnit() {
			if (this.unit) return this.unit

			const unit = this.field?.control?.settings?.unit
			if (!unit) return null

			if (unit === 'percent' || unit === '%') return '%'
			if (unit === 'currency') {
				return this.$store.state.selectedClient?.fields?.currency?.de || '€'
			}

			const key = 'text.unit-' + unit
			const r = this.$t(key)
			if (r == key) return unit
			return r
		},
		effectiveDialogTitle() {
			if (this.dialogTitle) return this.dialogTitle
			const title = this.field?.control?.settings?.dialogTitle
			if (title) return this.$t('text.' + title)
			return this.$t('text.editValue')
		},
		effectiveDialogText() {
			if (this.dialogText) return this.dialogText
			const text = this.field?.control?.settings?.dialogText
			if (text) return this.$t('text.' + text)
			return this.$t('text.editValueWarning')
		}
	},
	watch: {
		modelValue(n) {
			this.model = n
			if (!this.isEditing) {
				this.editValue = n
			}
			this.validate()
		},
		model(n) {
			if (!this.isEditing) {
				this.editValue = n
			}
			this.$emit('update:modelValue', n)
		}
	},
	methods: {
		validate() {
			this.onErrors([
				this.validateRequired(),
				this.validateRangeMax(),
				this.validateRangeMin()
			])
		},
		openEditDialog() {
			if (this.showDialog) {
				this.$refs.editDialog.show = true
			} else {
				this.startEdit()
			}
		},
		onEditConfirmed() {
			this.$refs.editDialog.show = false
			this.startEdit()
		},
		startEdit() {
			this.originalValue = this.model
			this.editValue = this.model
			this.isEditing = true
			this.$nextTick(() => {
				const input = this.$el.querySelector('input')
				if (input) {
					input.focus()
					input.select()
				}
			})
		},
		saveChanges() {
			if (this.fieldType === 'Number' && this.editValue !== '') {
				this.editValue = Number(this.editValue)
			}

			this.model = this.editValue
			this.isEditing = false

			// Emit update:modelValue first to ensure parent component updates
			this.$emit('update:modelValue', this.model)

			// Use nextTick to ensure the parent has processed the v-model update
			this.$nextTick(() => {
				this.$emit('save', this.model)
			})
		},
		cancelEdit() {
			this.editValue = this.originalValue
			this.isEditing = false
		},
		checkPercentage(value, event) {
			const char = String.fromCharCode(event.which || event.keyCode)
			const newValue = value + char

			if (!/^\d*\.?\d*$/.test(newValue)) {
				event.preventDefault()
				return false
			}

			const num = parseFloat(newValue)
			if (!isNaN(num) && (num < 0 || num > 100)) {
				event.preventDefault()
				return false
			}
		}
	},
	mounted() {
		this.model = this.modelValue
		this.editValue = this.modelValue
		this.validate()
	}
}
</script>

<style lang="scss" scoped>
.InlineEditField {
	--col-line: #cfd9e0;
	--col-line-focus: #036fe3;
	--col-error: #da294a;

	.inline-edit-container {
		display: flex;
		align-items: center;
		gap: 16px;
		min-height: 40px;

		.field-input {
			flex: 0 0 200px;

			.number-input-wrapper {
				position: relative;
				display: flex;
				align-items: center;

				input {
					padding-right: 30px;
				}

				.unit {
					position: absolute;
					right: 12px;
					color: #666;
					pointer-events: none;
					font-size: 14px;
				}
			}

			.input {
				width: 100%;
				outline: none;
				box-sizing: border-box;
				background-color: white;
				border: 1px solid var(--col-line);
				border-radius: 4px;
				color: rgb(65, 77, 99);
				font-size: 14px;
				line-height: 20px;
				padding: 8px 12px;
				transition: all 0.2s ease;

				&:focus {
					border-color: rgb(0, 89, 200);
					box-shadow: rgb(152 203 255) 0px 0px 0px 3px;
				}

				&:disabled {
					background-color: #f5f5f5;
					color: #999;
					cursor: not-allowed;
				}
			}

			input[type=number]::-webkit-outer-spin-button,
			input[type=number]::-webkit-inner-spin-button {
				-webkit-appearance: none;
				margin: 0;
			}

			input[type=number] {
				-moz-appearance: textfield;
			}
		}

		.field-action {
			flex: 0 0 auto;
			display: flex;
			align-items: center;

			button {
				font-size: 15px;
				font-weight: 500;
			}

			.edit-btn,
			.save-btn {
				letter-spacing: normal;
				text-transform: none;
			}

			.edit-btn {
				opacity: 0.8;
				transition: opacity 0.2s;

				&:hover {
					opacity: 1;
				}
			}

			.save-btn {
				white-space: nowrap;
			}
		}
	}

	&.editing {
		.field-input .input {
			background-color: #fff;
			border-color: var(--col-line-focus);
		}
	}

	&.error {
		.field-input .input {
			border-color: var(--col-error);

			&:focus {
				box-shadow: rgb(255 177 178) 0px 0px 0px 3px;
			}
		}
	}
}

.warningDialogImage {
	width: 64px;
	height: 64px;
}

@media (max-width: 768px) {
	.InlineEditField {
		.inline-edit-container {
			flex-wrap: wrap;
			gap: 12px;

			.field-input {
				flex: 1 1 auto;
				min-width: 150px;
			}

			.field-action {
				flex: 0 0 auto;
			}
		}
	}
}
</style>